<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

interface ComparisonList {
  title: string;
  items: string[];
}

const props = withDefaults(
  defineProps<{
    heading: string;
    description: string;
    leftList: ComparisonList;
    rightList: ComparisonList;
    quote?: string;
    ctaText?: string;
    subcopyClass?: string;
    appendClass?: string;
  }>(),
  {
    ctaText: "Get Started",
    subcopyClass: "",
    appendClass: "",
  },
);

defineEmits(["cta-clicked"]);

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

const maxItems = computed(() =>
  Math.max(props.leftList.items.length, props.rightList.items.length),
);

const pairedRows = computed(() => {
  const rows: { left: string; right: string }[] = [];
  for (let i = 0; i < maxItems.value; i++) {
    rows.push({
      left: props.leftList.items[i] ?? "",
      right: props.rightList.items[i] ?? "",
    });
  }
  return rows;
});
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] text-center relative overflow-hidden"
  >

    <!-- Heading -->
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant-demi lg:whitespace-nowrap"
    >
      <WrGradientText :html="heading" append-class=" pb-0 lg:pb-4" />
    </h2>
    <p
      class="self-stretch text-center mt-4 px-4 text-[16px] lg:text-[18px] lg:opacity-70 mx-auto font-avant font-normal leading-[22px] tracking-[0.36px]"
      :class="[subcopyClass || 'max-w-[600px]', isLight ? 'text-black' : 'text-white']"
    >
      {{ description }}
    </p>

    <!-- Comparison Table -->
    <div
      class="relative z-10 mt-12 mx-auto px-4 max-w-[964px]"
    >
      <div
        :class="[
          'rounded-2xl overflow-hidden border',
          isLight
            ? 'bg-white/80 border-gray-200/60'
            : 'bg-[#1B022B]/90 border-white/10',
        ]"
      >
        <!-- Desktop Table Header (two columns side-by-side) -->
        <div
          class="hidden lg:grid grid-cols-2 divide-x"
          :class="isLight ? 'divide-gray-200' : 'divide-white/10'"
        >
          <!-- Left Column Header (Problem) -->
          <div
            class="px-8 py-5 text-right"
            :class="isLight ? 'bg-red-50/80' : 'bg-red-950/20'"
          >
            <div class="flex items-center justify-end gap-2">
              <h3
                class="font-semibold font-avant-demi text-[20px] leading-normal"
                :class="isLight ? 'text-red-700' : 'text-red-400'"
              >
                {{ leftList.title }}
              </h3>
              <span
                class="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold"
                :class="isLight ? 'bg-red-100 text-red-600' : 'bg-red-900/40 text-red-400'"
              >
                ✕
              </span>
            </div>
          </div>

          <!-- Right Column Header (Solution) -->
          <div
            class="px-8 py-5"
            :class="isLight ? 'bg-emerald-50/80' : 'bg-emerald-950/20'"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold"
                :class="isLight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-900/40 text-emerald-400'"
              >
                ✓
              </span>
              <h3
                class="font-semibold font-avant-demi text-[20px] leading-normal"
                :class="isLight ? 'text-emerald-700' : 'text-emerald-400'"
              >
                {{ rightList.title }}
              </h3>
            </div>
          </div>
        </div>

        <!-- Desktop: Two-column grid with paired rows -->
        <div
          class="hidden lg:block divide-y"
          :class="isLight ? 'divide-gray-100' : 'divide-white/5'"
        >
          <template v-for="(row, index) in pairedRows" :key="'desktop-' + index">
            <div
              class="grid grid-cols-2 divide-x items-stretch transition-colors duration-200"
              :class="[
                isLight
                  ? 'divide-gray-200 hover:bg-gray-50/50'
                  : 'divide-white/10 hover:bg-white/5',
              ]"
            >
              <!-- Left Cell (Problem) -->
              <div
                class="px-6 lg:px-8 py-5 text-right transition-colors duration-200"
                :class="[
                  isLight
                    ? 'hover:bg-red-50/60'
                    : 'hover:bg-red-950/10',
                  index % 2 === 0
                    ? isLight
                      ? 'bg-white'
                      : 'bg-white/[0.02]'
                    : isLight
                      ? 'bg-gray-50/40'
                      : 'bg-white/[0.01]',
                ]"
              >
                <div
                  v-if="row.left"
                  class="flex items-center gap-3 justify-end"
                >
                  <span
                    class="font-avant text-[15px] font-normal leading-normal tracking-[0.75px]"
                    :class="isLight ? 'text-gray-700' : 'text-white/80'"
                  >
                    {{ row.left }}
                  </span>
                  <span
                    class="flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-xs"
                    :class="isLight ? 'bg-red-100 text-red-500' : 'bg-red-900/40 text-red-400'"
                  >
                    ✕
                  </span>
                </div>
              </div>

              <!-- Right Cell (Solution) -->
              <div
                class="px-6 lg:px-8 py-5 transition-colors duration-200"
                :class="[
                  isLight
                    ? 'hover:bg-emerald-50/60'
                    : 'hover:bg-emerald-950/10',
                  index % 2 === 0
                    ? isLight
                      ? 'bg-white'
                      : 'bg-white/[0.02]'
                    : isLight
                      ? 'bg-gray-50/40'
                      : 'bg-white/[0.01]',
                ]"
              >
                <div
                  v-if="row.right"
                  class="flex items-center gap-3"
                >
                  <span
                    class="flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-xs"
                    :class="isLight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-900/40 text-emerald-400'"
                  >
                    ✓
                  </span>
                  <span
                    class="font-avant text-[15px] font-normal leading-normal tracking-[0.75px]"
                    :class="isLight ? 'text-gray-700' : 'text-white/80'"
                  >
                    {{ row.right }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Mobile: Stacked — Problem header → Problems → Solution header → Solutions -->
        <div class="lg:hidden">
          <!-- Problem Header -->
          <div
            class="px-4 py-4 flex items-center gap-2"
            :class="isLight ? 'bg-red-50/80' : 'bg-red-950/20'"
          >
            <span
              class="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold"
              :class="isLight ? 'bg-red-100 text-red-600' : 'bg-red-900/40 text-red-400'"
            >
              ✕
            </span>
            <h3
              class="font-semibold font-avant-demi text-[16px] leading-normal"
              :class="isLight ? 'text-red-700' : 'text-red-400'"
            >
              {{ leftList.title }}
            </h3>
          </div>

          <!-- Problems column -->
          <div
            v-for="(item, index) in leftList.items"
            :key="'mobile-prob-' + index"
            class="px-4 py-3 flex items-center gap-3 border-t"
            :class="[
              isLight ? 'border-red-200/50 bg-red-50/60' : 'border-red-900/30 bg-red-950/10',
            ]"
          >
            <span
              class="flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-xs"
              :class="isLight ? 'bg-red-100 text-red-500' : 'bg-red-900/40 text-red-400'"
            >
              ✕
            </span>
            <span
              class="font-avant text-[14px] font-normal leading-normal tracking-[0.5px] text-left"
              :class="isLight ? 'text-gray-700' : 'text-white/80'"
            >
              {{ item }}
            </span>
          </div>

          <!-- Solution Header -->
          <div
            class="px-4 py-4 flex items-center gap-2"
            :class="isLight ? 'bg-emerald-50/80' : 'bg-emerald-950/20'"
          >
            <span
              class="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold"
              :class="isLight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-900/40 text-emerald-400'"
            >
              ✓
            </span>
            <h3
              class="font-semibold font-avant-demi text-[16px] leading-normal"
              :class="isLight ? 'text-emerald-700' : 'text-emerald-400'"
            >
              {{ rightList.title }}
            </h3>
          </div>

          <!-- Solutions column -->
          <div
            v-for="(item, index) in rightList.items"
            :key="'mobile-sol-' + index"
            class="px-4 py-3 flex items-center gap-3 border-t"
            :class="[
              isLight ? 'border-emerald-200/50 bg-emerald-50/60' : 'border-emerald-900/30 bg-emerald-950/10',
            ]"
          >
            <span
              class="flex-shrink-0 w-5 h-5 rounded-full inline-flex items-center justify-center text-xs"
              :class="isLight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-900/40 text-emerald-400'"
            >
              ✓
            </span>
            <span
              class="font-avant text-[14px] font-normal leading-normal tracking-[0.5px] text-left"
              :class="isLight ? 'text-gray-700' : 'text-white/80'"
            >
              {{ item }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Key Point / Quote -->
    <div v-if="quote" class="mt-12 max-w-[700px] mx-auto px-4">
      <p
        class="text-center italic font-avant text-[18px] lg:text-[20px] leading-relaxed"
        :class="isLight ? 'text-[#501D71]' : 'text-[#CDA0FE]'"
      >
        "{{ quote }}"
      </p>
    </div>

    <!-- CTA Button -->
    <div v-if="ctaText" class="flex justify-center mt-16">
      <WrButton
        @click="$emit('cta-clicked')"
        container-class="sm:h-[48px]"
        append-class="text-base font-normal px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
